import { useCallback, useState, type MouseEvent } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import DescriptionIcon from '@material-ui/icons/Description';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import {
  alertApiRef,
  errorApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { techdocsRdapClientApiRef } from '../apis/techdocsRdapClient';
import { techdocsRdapVersionStoreApiRef } from '../apis/versionStore';

// Kept in sync with packages/app/src/styles.css, which keys the Docs tab's
// full-width layout off `:has([aria-controls='tech-docs-reader-page-actions-header'])`.
const ACTIONS_MENU_ID = 'tech-docs-reader-page-actions-header';

type ExportFormat = 'pdf' | 'docx';

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * The "⋮" actions menu shown in the TechDocs toolbar. Currently offers a
 * full-site export (every nav page in one file) as PDF or Word, honouring the
 * branch/tag selected in the version picker.
 */
export const TechDocsActionsMenu = () => {
  const { entity } = useEntity();
  const client = useApi(techdocsRdapClientApiRef);
  const versionStore = useApi(techdocsRdapVersionStoreApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      const entityId = {
        kind: entity.kind,
        namespace: entity.metadata.namespace ?? 'default',
        name: entity.metadata.name,
      };
      setBusy(format);
      setAnchorEl(null);
      alertApi.post({
        message: `Preparing ${
          format === 'pdf' ? 'PDF' : 'Word'
        } export — this can take a while for large docs…`,
        severity: 'info',
        display: 'transient',
      });
      try {
        const ref = versionStore.get(entityId);
        const { blob, filename } = await client.exportEntityDocs(
          entityId,
          ref,
          format,
        );
        triggerDownload(blob, filename);
      } catch (error) {
        errorApi.post(error as Error);
      } finally {
        setBusy(null);
      }
    },
    [entity, client, versionStore, alertApi, errorApi],
  );

  return (
    <>
      <Tooltip title="Actions">
        <span>
          <IconButton
            aria-controls={ACTIONS_MENU_ID}
            aria-haspopup="true"
            aria-label="TechDocs actions"
            onClick={handleOpen}
            disabled={busy !== null}
            size="small"
          >
            {busy !== null ? (
              <CircularProgress size={20} />
            ) : (
              <MoreVertIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        id={ACTIONS_MENU_ID}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        getContentAnchorEl={null}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        keepMounted
      >
        <MenuItem
          onClick={() => handleExport('pdf')}
          disabled={busy !== null}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <PictureAsPdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export as PDF" />
        </MenuItem>
        <MenuItem
          onClick={() => handleExport('docx')}
          disabled={busy !== null}
        >
          <ListItemIcon style={{ minWidth: 36 }}>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Export as Word (.docx)" />
        </MenuItem>
      </Menu>
    </>
  );
};
